import { t, Trans } from "@lingui/macro";
import { LockKeyIcon } from "@phosphor-icons/react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@reactive-resume/ui";
import { useEffect } from "react";
import { Link } from "react-router";

import { defaultIntervalFor, UpgradeButton } from "@/client/components/billing/upgrade-button";
import { useUpgradePromptViewed } from "@/client/hooks/use-upgrade-prompt-viewed";
import { axios } from "@/client/libs/axios";
import { translatePaywall } from "@/client/services/errors/translate-paywall";
import { useUser } from "@/client/services/user";
import { closePaywall, usePaywallStore } from "@/client/stores/paywall";

/**
 * Global response to a server paywall rejection.
 *
 * Mounted once in `DialogProvider`, which wraps `<Outlet/>` — so it is live on
 * every route rather than dashboard-only, and a single axios interceptor edit
 * covers every call site in the app.
 *
 * A dialog rather than a toast on purpose: `TOAST_LIMIT` is 1 and a new toast
 * evicts the old one on a 5 s timer. A purchase decision cannot live there.
 */
export const PaywallDialog = () => {
  const signal = usePaywallStore((state) => state.signal);
  const { user } = useUser();

  // Only ENTITLEMENT_REQUIRED carries `currentTier` from the server; every
  // quota rejection (`usage-limit.service.ts`) omits it, so `tierFor` would
  // fall through to PRO and offer a PRO subscriber the plan they already own.
  // A TRIALING user is on the PRO trial tier but owns nothing, so they still
  // get sold PRO.
  const subscription = user?.subscription;
  const ownedTier = subscription?.status === "TRIALING" ? undefined : subscription?.tier;
  const copy = signal
    ? translatePaywall({ ...signal, currentTier: signal.currentTier ?? ownedTier })
    : null;
  const offeredTier = copy?.tier;

  // GTM §6 funnel stage — the door was built (Aug audit) but nobody could
  // say whether anyone ever reached it. Fires once per open (a fresh `signal`
  // object each time `openPaywall` runs), fire-and-forget. Carries the offer
  // (`surface`, `plan`, `interval`) so the paywall can be read against the
  // checkout it leads to (spec 048 FR-017). The server adds `status`.
  useUpgradePromptViewed(signal && offeredTier ? "paywall_dialog" : undefined);
  useEffect(() => {
    if (!signal || !offeredTier) return;
    void axios
      .post("/analytics/track", {
        event: "paywall_viewed",
        props: {
          surface: "paywall_dialog",
          plan: offeredTier,
          interval: defaultIntervalFor(offeredTier),
        },
      })
      .catch(() => null);
  }, [signal, offeredTier]);

  if (!signal || !copy) return null;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) closePaywall();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <LockKeyIcon size={20} weight="duotone" />
          </div>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          {/* An ULTIMATE subscriber can still hit a quota wall (the 25/day
              submission cap). There is no plan above theirs to sell, and the
              billing webhook upserts Subscription by userId — so opening any
              checkout here would double-bill AND downgrade them. */}
          {ownedTier !== "ULTIMATE" && (
            <UpgradeButton
              tier={copy.tier}
              source="paywall_dialog"
              ariaLabel={copy.cta}
              className="w-full"
              onOpen={closePaywall}
            >
              {copy.cta}
            </UpgradeButton>
          )}
          <Button asChild variant="ghost" size="sm" className="w-full">
            <Link to="/pricing?utm_source=app&utm_medium=paywall_dialog" onClick={closePaywall}>
              <Trans>Compare all plans</Trans>
            </Link>
          </Button>
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" aria-label={t`Not now`} onClick={closePaywall}>
            <Trans>Not now</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
